import React, { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../../components/ui/card';
import { cn } from '../../../../../../../../lib/utils';
import { formatLabel, StatusBadge } from '../../pipeMillReportUtils';
import {
  AdjudicationPanel,
  BendDispositionPanel,
  ChemicalFormulaRow,
  ChemistryResultRow,
  FlatteningDispositionPanel,
  HydrostaticDispositionPanel,
  ItemActionButtons,
  KeyValueRows,
  MiniStatusDot,
  PipeLocationDiagram,
  ReportMetadataRail,
  ResultRow,
  SectionHead,
  StatCard,
  TensileResultRow,
} from './shared/components';
import {
  AUT_DETAIL,
  BEND_DETAIL,
  CHEMICAL_DETAIL,
  CVN_DETAIL,
  DIMENSIONAL_DETAIL,
  HARDNESS_DETAIL,
  HYDROSTATIC_DETAIL,
  STANDARD_REPORT_DETAILS,
} from './shared/sampleData';
import {
  average,
  formatDisplayDate,
  getStandardReportSummaryStats,
  standardLimitText,
  standardScaleMax,
  standardScaleMin,
  statusNoteClassName,
} from './shared/format';
import {
  createSampleMeasurement,
  isSampleDataDeleted,
} from './shared/measurements';
import {
  getProjectLimitField,
  getProjectLimitMax,
  getProjectLimitMin,
  getProjectLimitRows,
  getProjectLimitText,
} from './shared/limits';
import type {
  ChemicalElementDisplay,
  FullReportType,
  ProjectLimitFieldOption,
  ReportDefinition,
  ReportMeasurementItem,
  ReportMetadataEntry,
  ReportSummary,
  StandardCheck,
} from './shared/types';
import type { BaseReportDetailProps } from './shared/reportProps';

export const WeldMapReportDetail: React.FC<{
  heatNumber: string;
  metadata?: ReportMetadataEntry;
  projectLimitFields?: ProjectLimitFieldOption[];
  frequency?: string;
  measurements?: ReportMeasurementItem[];
  deletedMeasurementIds?: string[];
  onAddMeasurement?: (reportType: FullReportType) => void;
  onEditMeasurement?: (measurement: ReportMeasurementItem) => void;
  onDeleteMeasurement?: (measurement: ReportMeasurementItem) => void;
}> = ({ heatNumber, metadata, projectLimitFields = [], frequency, measurements = [], onAddMeasurement, onEditMeasurement, onDeleteMeasurement }) => {
  const weldMeasurements = measurements.filter((measurement) => measurement.reportType === 'WELD_MAP');
  const [activeWeldId, setActiveWeldId] = useState<string | null>(null);
  useEffect(() => {
    if (!weldMeasurements.length) {
      setActiveWeldId(null);
      return;
    }

    if (!activeWeldId || !weldMeasurements.some((measurement) => measurement.id === activeWeldId)) {
      setActiveWeldId(weldMeasurements[0].id);
    }
  }, [activeWeldId, weldMeasurements]);
  const activeWeld = weldMeasurements.find((measurement) => measurement.id === activeWeldId) || weldMeasurements[0];
  const effectiveFrequency = metadata?.frequency || frequency || 'per_pipe';
  const heatInputMin = getProjectLimitMin(projectLimitFields, 'weld_heat_input_kj_mm', 1);
  const heatInputMax = getProjectLimitMax(projectLimitFields, 'weld_heat_input_kj_mm', 2.5);
  const heatInputLimitText = getProjectLimitText(projectLimitFields, 'weld_heat_input_kj_mm', `${heatInputMin} - ${heatInputMax} kJ/mm`);
  const plateHeatLimitText = getProjectLimitText(projectLimitFields, 'weld_plate_heat_match', 'must match pipe heat');
  const wpsLimitText = getProjectLimitText(projectLimitFields, 'weld_wps_ref', 'NRI-SAW-014');
  const projectLimitRows = getProjectLimitRows(projectLimitFields, [
    ['Heat input', heatInputLimitText, true],
    ['Plate heat', plateHeatLimitText, true],
    ['WPS', wpsLimitText, true],
  ]);
  const trace = {
    steelMill: metadata?.lab || '-',
    plateId: activeWeld?.weldPlateId || '-',
    plateHeat: activeWeld?.weldPlateHeatNumber || '-',
    pipeHeat: activeWeld?.weldPipeHeatNumber || heatNumber,
    pipeNo: heatNumber || '-',
    wps: activeWeld?.weldWpsRef || '-',
  };
  const traceComplete = Boolean(activeWeld && trace.plateHeat !== '-' && trace.pipeHeat && trace.pipeHeat !== '-');
  type WeldMapPass = {
    id: string;
    pass: string;
    process: string;
    wireHeat: string;
    fluxLot: string;
    current: number;
    voltage: number;
    speed: number;
    heatInput: number;
    status: string;
    measurement?: ReportMeasurementItem;
  };
  const passes: WeldMapPass[] = weldMeasurements.map((measurement) => {
    const current = Number(measurement.weldCurrentA || 0);
    const voltage = Number(measurement.weldVoltageV || 0);
    const speed = Number(measurement.weldSpeedMmMin || 0);
    const calculatedHeatInput = current && voltage && speed ? (current * voltage * 60) / (speed * 1000) : Number(measurement.weldHeatInputKjMm || 0);
    const heatInput = Number(measurement.weldHeatInputKjMm || calculatedHeatInput || Number.NaN);
    const traceMatches = (measurement.weldPlateHeatNumber || '').trim() === (measurement.weldPipeHeatNumber || '').trim();
    const status = !Number.isFinite(heatInput) || !measurement.weldPlateHeatNumber || !measurement.weldPipeHeatNumber || !measurement.weldWpsRef
      ? 'PENDING_REVIEW'
      : heatInput < heatInputMin || heatInput > heatInputMax || !traceMatches
        ? 'FAIL'
        : measurement.status;

    return {
      id: measurement.id,
      pass: measurement.weldPassName || measurement.fieldLabel || 'weld pass',
      process: measurement.weldProcess || '-',
      wireHeat: measurement.weldWireHeatNo || '-',
      fluxLot: measurement.weldFluxLotNo || '-',
      current,
      voltage,
      speed,
      heatInput,
      status,
      measurement,
    };
  });
  const traceMatches = traceComplete && trace.plateHeat === trace.pipeHeat;
  const overallStatus = !passes.length ? 'PENDING_REVIEW' : passes.some((pass) => pass.status === 'FAIL') || !traceMatches ? 'FAIL' : passes.some((pass) => pass.status === 'PENDING_REVIEW') ? 'PENDING_REVIEW' : 'PASS';

  const ChainNode = ({ title, sub, accent }: { title: string; sub: string; accent?: boolean }) => (
    <div className={cn('min-w-0 flex-1 rounded-lg border p-3', accent ? 'border-[#5A86A7] bg-[#DCEEF8]' : 'border-[#E2E5EA] bg-white')}>
      <div className={cn('truncate font-mono text-[12px] font-semibold', accent ? 'text-[#285A7A]' : 'text-blueprint-blue')}>{title}</div>
      <div className="mt-1 truncate text-[10px] text-heritage-grey">{sub}</div>
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px]">
      <main className="space-y-5 p-6">
        {traceComplete && !traceMatches && (
          <div className="rounded-lg border border-[#D64D55] bg-[rgba(214,77,85,0.08)] p-3 text-[13px] text-readable-red">
            <span className="font-semibold">Plate heat does not match pipe heat.</span>
            <span className="ml-2">Plate: {trace.plateHeat} · Pipe: {trace.pipeHeat}</span>
          </div>
        )}

        <div>
          <SectionHead title="Plate-to-pipe chain" subtitle="Material flows from the steel-maker plate through SAW into the finished pipe" />
          <Card className="border-[#E2E5EA]">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 items-stretch gap-3 md:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr_auto_1fr]">
                <ChainNode title="Steel mill" sub={trace.steelMill} />
                <div className="hidden items-center text-heritage-grey md:flex">&rarr;</div>
                <ChainNode title={`Heat ${trace.plateHeat}`} sub="ladle cast" />
                <div className="hidden items-center text-heritage-grey md:flex">&rarr;</div>
                <ChainNode title={`Plate ${trace.plateId}`} sub="rolled & sheared" accent />
                <div className="hidden items-center text-heritage-grey md:flex">&rarr;</div>
                <ChainNode title={trace.wps} sub="approved WPS" />
                <div className="hidden items-center text-heritage-grey md:flex">&rarr;</div>
                <ChainNode title={`Pipe ${trace.pipeNo}`} sub={`heat ${trace.pipeHeat}${traceComplete && !traceMatches ? ' mismatch' : ''}`} accent />
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <div className="flex flex-wrap items-end justify-between gap-3 p-4">
            <SectionHead title="Weld passes" subtitle={`${passes.length} saved passes · heat input must be within ${heatInputLimitText}`} />
              <Button type="button" size="sm" onClick={() => onAddMeasurement?.('WELD_MAP')} className="bg-hero-red text-white hover:bg-hero-red-60">
                <Plus className="mr-2 h-4 w-4" />
                Add Weld Pass
              </Button>
            </div>
            <table className="w-full text-left">
              <thead className="border-y border-[#E2E5EA] bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                <tr>
                  <th className="px-4 py-3">Pass</th>
                  <th className="px-4 py-3">Process</th>
                  <th className="px-4 py-3">Wire heat</th>
                  <th className="px-4 py-3">Flux lot</th>
                  <th className="px-4 py-3 text-right">Current (A)</th>
                  <th className="px-4 py-3 text-right">Voltage (V)</th>
                  <th className="px-4 py-3 text-right">Speed</th>
                  <th className="px-4 py-3 text-right">Heat input</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF0F3] text-[12px] text-text-grey">
                {!passes.length && (
                  <tr>
                    <td colSpan={10} className="px-4 py-6 text-center text-[12px] text-heritage-grey">
                      No weld passes saved yet. Add a weld pass to populate this report.
                    </td>
                  </tr>
                )}
                {passes.map((pass) => (
                  <tr key={pass.id} onClick={() => pass.measurement && setActiveWeldId(pass.id)} className={cn('hover:bg-page-bg', pass.measurement && 'cursor-pointer', activeWeldId === pass.id && 'bg-[#E7F3FF]')}>
                    <td className="px-4 py-3 font-semibold text-blueprint-blue">{pass.pass}</td>
                    <td className="px-4 py-3 font-mono">{pass.process}</td>
                    <td className="px-4 py-3 font-mono">{pass.wireHeat}</td>
                    <td className="px-4 py-3 font-mono">{pass.fluxLot}</td>
                    <td className="px-4 py-3 text-right font-mono">{pass.current || '-'}</td>
                    <td className="px-4 py-3 text-right font-mono">{pass.voltage || '-'}</td>
                    <td className="px-4 py-3 text-right font-mono">{pass.speed || '-'}</td>
                    <td className="px-4 py-3 text-right font-mono">{Number.isFinite(pass.heatInput) ? pass.heatInput.toFixed(2) : '-'} kJ/mm</td>
                    <td className="px-4 py-3"><StatusBadge status={pass.status} /></td>
                    <td className="px-4 py-3 text-right">
                      {pass.measurement && (
                        <ItemActionButtons measurement={pass.measurement} onEditMeasurement={onEditMeasurement} onDeleteMeasurement={onDeleteMeasurement} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <div>
          <SectionHead title="Heat-input formula" subtitle="HI = (I x V x 60) / (speed x 1000) - within WPS window" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {passes.map((pass) => {
              const hasHeatInput = Number.isFinite(pass.heatInput);
              const pct = hasHeatInput ? ((pass.heatInput - heatInputMin) / (heatInputMax - heatInputMin)) * 100 : 0;
              const inRange = hasHeatInput && pass.heatInput >= heatInputMin && pass.heatInput <= heatInputMax;
              return (
                <Card key={`${pass.id}-heat`} className="border-[#E2E5EA] bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">{pass.pass}</div>
                      <div className="font-mono text-[11px] font-semibold text-blueprint-blue">{pass.process}</div>
                    </div>
                    <div className="mt-3 flex items-baseline gap-1">
                      <span className="font-mono text-[22px] font-semibold text-blueprint-blue">{hasHeatInput ? pass.heatInput.toFixed(2) : '-'}</span>
                      <span className="text-[10px] text-heritage-grey">kJ/mm</span>
                    </div>
                    <div className="mt-3 h-2 rounded-full bg-[#EEF0F3]">
                      <div className="relative h-2 rounded-full bg-[rgba(0,158,96,0.16)]">
                        <span className={cn('absolute top-1/2 h-4 w-1 -translate-y-1/2 rounded-full', inRange ? 'bg-galvanizing-green' : 'bg-hero-red')} style={{ left: `calc(${Math.min(100, Math.max(0, pct))}% - 2px)` }} />
                      </div>
                    </div>
                    <div className="mt-2 flex justify-between font-mono text-[10px] text-heritage-grey">
                      <span>{heatInputMin}</span>
                      <span>{heatInputMax}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </main>

      <ReportMetadataRail
        rows={[
          ['Report ID', metadata?.reportId || '-', true],
          ['Performed at', formatDisplayDate(metadata?.performedAt), true],
          ['Heat', heatNumber, true],
          ['Lab', metadata?.lab || '-'],
          ['Technician', metadata?.technician || '-'],
          ['Source', metadata?.source ? formatLabel(metadata.source) : '-'],
          ['Source file', metadata?.sourceFile || '-', true],
        ]}
        extraTitle="Limits In Effect"
        extraRows={projectLimitRows}
      >
        <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Frequency / Scope</div>
        <Card className="mt-3 border-[#E2E5EA]">
          <CardContent className="p-4">
            <KeyValueRows rows={[['Frequency', effectiveFrequency, true], ['Passes', `${passes.length} saved`, true], ['Status', <StatusBadge key="status" status={overallStatus} />]]} />
          </CardContent>
        </Card>
        <AdjudicationPanel />
      </ReportMetadataRail>
    </div>
  );
};